import { useState } from 'react';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { getFeatureValueBySubscription, getSubscriptionByCountryAndProductType } from '@utils/FilterUtils';
import { DataType, FilterKeys, Filters, FilterTitle, Option, PageType, ResponseConfig } from '@utils/pageConfig/Types';
import { PagesConfig } from '@utils/pageConfig/PagesConfigContext';
import { ProductType } from '@pages/product-analyzer/Product';
import { MainFilters } from '@services/FiltersTypes';
import useDebouncedEffect from '@hooks/UseDebouncedEffect';
import { InitialFilter } from '@state/reducers/initial-filter/InitialFiltersReducer';
import {isMultiPgCommonFeatureAllowed} from "@utils/filterTrayUtils/FilterTrayUtils";

const getSelectedCountries = (selectedCountries: Option[], pagesConfig: PagesConfig) => {
  const configFilters = pagesConfig.selectedFilters[pagesConfig.pageType].selectedFilters;
  const countryIndex = configFilters.findIndex((filter) => filter.filterKey === FilterKeys.COUNTRY);
  if (countryIndex !== -1) {
    selectedCountries = configFilters[countryIndex].options;
  }
  return selectedCountries;
};

/**
 * React hook to call feature-value API and sets values for side menu selectors
 * @param param0 clientId, selectedCountries, timeFrame
 * @returns data, error, loading
 */
export const useSideMenuFilters = ({
  clientId,
  selectedCountries,
  selectedProducts,
  timeFrame,
  appliedProductGroups,
  responseConfig,
  pagesConfig,
  initialFilters,
  currentTotalName,
  productType,
}: {
  clientId: number;
  selectedCountries: Option[];
  selectedProducts: Option[] | undefined;
  timeFrame: Periodicity;
  appliedProductGroups: Option[];
  responseConfig: ResponseConfig;
  pagesConfig: PagesConfig;
  initialFilters: MainFilters[];
  currentTotalName: string;
  productType: ProductType;
}) => {
  selectedCountries = getSelectedCountries(selectedCountries, pagesConfig);

  const [data, setData] = useState<Filters[]>([]);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const hasSelectedProducts = (selectedProductArr: Option[] | undefined) => {
    return !selectedProductArr || (selectedProductArr && selectedProductArr.length > 1);
  };

  const callFeatureValueApi = async (periodicity: Periodicity, signal: AbortSignal) => {
    const allFilters = [...pagesConfig.appliedFilters[pagesConfig.pageType].selectedFilters];
    const subscriptions = getSubscriptionByCountryAndProductType(pagesConfig, selectedCountries, productType);
    const initialFilterForSelectedCountry = initialFilters.find(
      (filter: InitialFilter) => filter.country === selectedCountries[0].value,
    );

    try {
      setLoading(true);
      const includeCommonFeaturesOnly = isMultiPgCommonFeatureAllowed(pagesConfig.pageType);
      const featureValues = await getFeatureValueBySubscription(
        {
          productType: productType,
          selectedProductGroup: selectedProducts,
          initialFilters: initialFilters,
          selectedCountries: selectedCountries,
          pagesConfig: pagesConfig,
          responseConfig: responseConfig,
          allFilters: allFilters,
          timeFrame: periodicity,
          subscriptions: subscriptions,
          includeCommonFeaturesOnly: includeCommonFeaturesOnly,
        },
        timeFrame,
        currentTotalName,
        signal,
      );
      if (featureValues) {
        let features = featureValues;
        if ([PageType.EXPLORE, PageType.OPPORTUNITY_REVIEW].includes(pagesConfig.pageType)) {
          if (hasSelectedProducts(selectedProducts)) {
            if (PageType.OPPORTUNITY_REVIEW === pagesConfig.pageType) {
              features = features.filter((feature) => {
                return (
                  feature.filterKey !== FilterKeys.PRICEBAND
                );
              });
            }
            if (PageType.EXPLORE === pagesConfig.pageType) {
              features = features.filter((feature) => {
                return feature.filterKey !== FilterKeys.PRICEBAND;
              });
            }
          }
          features = features.filter((feature) => {
            if (feature.filterKey === FilterKeys.CHANNEL) {
              return (
                initialFilterForSelectedCountry?.isChannelFilterAllowed ||
                (appliedProductGroups && appliedProductGroups.length === 1)
              );
            } else {
              return true;
            }
          });
          const categoryFeatureOption: Filters = {
            dataType: DataType.CATEGORY,
            feature: FilterTitle.CATEGORY,
            filterTitle: FilterTitle.CATEGORY,
            filterKey: FilterKeys.CATEGORY,
            options: [],
            disabled: false,
          };
          const countryFeatureOption: Filters = {
            dataType: DataType.COUNTRIES,
            feature: FilterTitle.COUNTRY,
            filterTitle: FilterTitle.COUNTRY,
            filterKey: FilterKeys.COUNTRIES_FEATURE,
            options: [],
            disabled: false,
          };
          features.unshift(countryFeatureOption);
          features.unshift(categoryFeatureOption);
        }
        setData(features);
      }
      setLoading(false);
    } catch (err: any) {
      setError(err);
      setLoading(false);
    }
  };

  useDebouncedEffect(() => {
    const controller = new AbortController();
    callFeatureValueApi(timeFrame, controller.signal);

    return () => {
      controller.abort();
    };
  }, [clientId, selectedCountries, selectedProducts, timeFrame, pagesConfig.pageType]);

  return { data, error, loading };
};
